import * as undoActions from 'owa-mail-undo';
import copyMailListRows from '../../copyMailListRows';
import deleteMailListRows from '../../deleteMailListRows';
import folderStore, { Folder } from 'owa-folders';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import handleBlockActionForShadowMailbox from '../../helpers/handleBlockActionForShadowMailbox';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import { InstrumentationContext } from 'owa-mail-store';
import { default as getMoveKind, MoveKind } from './getMoveKind';
import { lazyAddFolderIdToMoveToMruList } from 'owa-mail-move-mru';
import { MailRowDataPropertyGetter, TableQueryType, TableView } from 'owa-mail-list-store';
import { returnTopExecutingActionDatapoint, DatapointStatus } from 'owa-analytics';
import { select } from 'satcheljs/lib/legacy';
import { userMailInteractionAction } from 'owa-mail-actions';
import {
    lazyMoveSharedFolderItems,
    lazyMoveSharedFolderConversations,
} from 'owa-mail-triage-action';
import { isSharedFolderItemMovementPermissible } from 'owa-mail-filterable-menu-behavior';
import { showNotificationOnMoveFailure } from 'owa-mail-filterable-menu-behavior';
import { getFolderIdFromTableView } from 'owa-mail-search';
import { ActionSource } from 'owa-analytics-actions';

export interface MoveMailListRowsHelperState {
    sourceFolder: Folder;
    destinationFolder: Folder;
    isUserShadowMailbox: boolean;
}

/**
 * Handler for move the rows
 */
export type moveRowsInternalHandler = (
    rowKeys: string[],
    destinationFolderId: string,
    originalTableView: TableView,
    actionSource: ActionSource
) => Promise<void>;

/**
 * Move rows from table action
 * @param rowKeys - the row keys of the rows that need to be moved
 * @param destinationFolderId - the folder they need to go to
 * @param originalTableView - the table view the rows are moving from
 */
export default select<MoveMailListRowsHelperState>({
    sourceFolder: (rowKeys: string[], destinationFolderId: string, originalTableView: TableView) =>
        folderStore.folderTable.get(originalTableView.tableQuery.folderId),

    destinationFolder: (
        rowKeys: string[],
        destinationFolderId: string,
        originalTableView: TableView
    ) => folderStore.folderTable.get(destinationFolderId),

    isUserShadowMailbox: () => getUserConfiguration().SessionSettings.IsShadowMailbox,
})(function moveMailListRowsHelper(
    rowKeys: string[],
    destinationFolderId: string,
    originalTableView: TableView,
    moveRowsInternal: moveRowsInternalHandler,
    actionSource: ActionSource,
    state?: MoveMailListRowsHelperState
): Promise<void> {
    const { sourceFolder, destinationFolder, isUserShadowMailbox } = state;
    const moveKind = getMoveKind(isUserShadowMailbox, sourceFolder, destinationFolder);
    const sourceFolderId = originalTableView.tableQuery.folderId;

    if (handleBlockActionForShadowMailbox(sourceFolderId, destinationFolderId)) {
        returnTopExecutingActionDatapoint().endWithError(
            DatapointStatus.UserError,
            new Error('Action not supported for shadow mailbox')
        );
        return Promise.resolve();
    }
    undoActions.clearLastUndoableAction();

    // Since row may have been deleted because moveRows is an lazy action,
    // we need to validate the row keys again before performing move
    // Here we cannot compare destinationFolderId with sourceFolderId, because sourceFolderId is null in Search,
    // so we need to get the actual parentFolderId of each row
    const filteredRowKeys = rowKeys.filter(
        rowKey =>
            originalTableView.rowKeys.indexOf(rowKey) >= 0 &&
            destinationFolderId !=
                MailRowDataPropertyGetter.getParentFolderId(rowKey, originalTableView)
    );

    const instrumentationContexts: InstrumentationContext[] = [];
    const isSearch = originalTableView.tableQuery.type == TableQueryType.Search;
    if (isSearch) {
        filteredRowKeys.forEach(rowKey => {
            const instrumentationContext = MailRowDataPropertyGetter.getInstrumentationContext(
                rowKey,
                originalTableView
            );
            if (instrumentationContext) {
                instrumentationContexts.push(instrumentationContext);
            }
        });
    }

    // We do not have any conversations that can be moved
    if (filteredRowKeys.length == 0) {
        returnTopExecutingActionDatapoint().endWithError(
            DatapointStatus.UserError,
            new Error('no conversations to move')
        );
        return Promise.resolve();
    }

    let promiseToReturn;
    switch (moveKind) {
        case MoveKind.ShadowMailbox_Delete:
            // if we are in a ShadowMailbox and actually need to perform a delete,
            // we call the deleteConversations action directly, which handles its own
            // service and undo operation
            deleteMailListRows(filteredRowKeys, originalTableView.id, false);
            return Promise.resolve();

        case MoveKind.ShadowMailbox_Copy:
            promiseToReturn = copyMailListRows(
                filteredRowKeys,
                originalTableView,
                destinationFolderId,
                sourceFolderId,
                actionSource
            );
            break;
        case MoveKind.SharedFolder:
            // Shared folder permission check for move action by drag and drop, if any of the source or destination folder
            // is shared folder with read-only permission, return immediately showing a failure notification
            if (
                !isSharedFolderItemMovementPermissible(
                    getFolderIdFromTableView(originalTableView),
                    destinationFolderId
                )
            ) {
                showNotificationOnMoveFailure(destinationFolderId);
                return Promise.resolve();
            }
            switch (originalTableView.tableQuery.listViewType) {
                case ReactListViewType.Conversation:
                    (async () => {
                        const moveSharedFolderConversations = await lazyMoveSharedFolderConversations.import();
                        moveSharedFolderConversations(
                            filteredRowKeys,
                            destinationFolderId,
                            originalTableView
                        );
                    })();
                    break;
                case ReactListViewType.Message:
                    (async () => {
                        const moveSharedFolderItems = await lazyMoveSharedFolderItems.import();
                        moveSharedFolderItems(
                            filteredRowKeys,
                            originalTableView.id,
                            destinationFolderId,
                            sourceFolderId
                        );
                    })();
                    break;
            }
            break;
        case MoveKind.Normal:
        default:
            // Update Move to mru list
            lazyAddFolderIdToMoveToMruList.importAndExecute(destinationFolderId);

            // Make service request
            promiseToReturn = moveRowsInternal(
                filteredRowKeys,
                destinationFolderId,
                originalTableView,
                actionSource
            );
            break;
    }
    userMailInteractionAction('Move', instrumentationContexts);
    return promiseToReturn;
});
